import { ChevronRightIcon, TruckIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/features/auth/auth-context"
import { useEwayPending } from "@/features/invoices/use-eway-pending"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * "THESE BILLS ARE STILL WAITING FOR AN E-WAY BILL NUMBER."
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE ONE THING ON THIS SCREEN THAT IS A JOB RATHER THAN A FIGURE.
 *
 * The CA logs into the government portal, generates the e-way bill, and types
 * the twelve digits back in here — settled with her in writing (§17.7, and see
 * `EwayBillCard.tsx`). `eway.record` is her write, and it is the only one she
 * has that a lorry waits on.
 *
 * IT SITS AT THE TOP OF REPORTS BECAUSE REPORTS IS WHERE SHE LANDS
 * (`routes/landing.ts`), and because it is not period-scoped: a consignment
 * waiting for paper does not stop waiting when the period selector says "last
 * month". Everything below it answers "what happened between two dates"; this
 * answers "what is open right now", so it goes above the selector rather than
 * under it, where a reader would reasonably assume the dates applied to it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT RENDERS NOTHING AT ALL WHEN THERE IS NOTHING TO DO — no header, no empty
 * state, no "0 waiting". §11.2's argument against a permanent "Notifications: 0"
 * row is the same one: a panel that is furniture on an ordinary day is furniture
 * the eye learns to skip, and then the day it holds three rows it gets skipped
 * too. On a settled day this component contributes zero pixels and the screen is
 * exactly the screen it was.
 *
 * NOT ROLE-GATED (§2.1). It is `can("eway.record")` and nothing else, so the
 * owner and the son see it too — both hold the capability, both receive the same
 * notification, and either of them may be the one at a keyboard when the number
 * comes through. Whoever has the sheet in front of them can clear the row; that
 * is the whole reason the capability is not routed through one person.
 * ─────────────────────────────────────────────────────────────────────────────
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function EwayPending(): React.JSX.Element | null {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  /*
   * `useAuth().can` rather than `useCan`, matching `EwayBillCard` and
   * `InvoiceDetailScreen`. Same map, same resolver (§2.1) — spelled this way so
   * that a suite stubbing the auth module with a bare `useAuth` does not break
   * the moment this panel is mounted inside a screen it already covers.
   */
  const canRecord = useAuth().can("eway.record")
  const { rows, loading, unavailable, unverified } = useEwayPending(canRecord)

  /*
   * NOTHING WHILE IT IS STILL FINDING OUT, and nothing when the module is off.
   * A skeleton here would be a promise that something is coming, on a panel that
   * is usually absent — which is a worse first impression of an empty queue than
   * silence is.
   */
  if (unavailable || loading || rows.length === 0) return null

  return (
    <Card data-testid="eway-pending">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TruckIcon aria-hidden="true" className="size-5 shrink-0" />
          {t("eway.pendingTitle")}
          <Badge variant="outline" className="tabular-nums">
            {rows.length}
          </Badge>
        </CardTitle>
        {/*
          * IT SAYS WHOSE JOB IT IS AND WHERE IT HAPPENS. This app cannot
          * generate an e-way bill and never will (turnover is far below the
          * threshold for portal API access), so a panel that merely said "these
          * are waiting" would invite somebody to look for a Generate button that
          * does not exist and must not.
          */}
        <CardDescription>{t("eway.pendingHelp")}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-0 p-0">
        <ul className="divide-border divide-y border-t">
          {rows.map((row) => (
            <li key={row.invoiceId}>
              {/*
                * THE WHOLE ROW IS THE LINK, and it goes to the BILL rather than
                * to a form of its own. The number is recorded on the invoice —
                * that is where the card, the threshold sentence and the audit
                * trail already live — and a second place to type it would be a
                * second place for it to be typed wrong.
                */}
              <Link
                to={routes.invoice(row.invoiceId)}
                className="hover:bg-muted/60 focus-visible:ring-ring flex min-h-11 w-full items-center gap-3 px-4 py-3 focus-visible:ring-2 focus-visible:outline-none"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold tabular-nums">{row.number}</span>
                    {row.customerName === null ? null : (
                      <span className="text-muted-foreground min-w-0 truncate text-sm">
                        {row.customerName}
                      </span>
                    )}
                  </span>

                  {/*
                    * BOTH FIGURES, NOT JUST "over the limit" — the same argument
                    * the notification makes. Someone who knows the intra-state
                    * limit is ₹1,00,000 and is looking at a ₹96,000 bill will
                    * ask whether the app is wrong; the consignment value
                    * INCLUDING tax is ₹1,13,280, and printing what was compared
                    * against what settles it here instead of in a phone call.
                    *
                    * Absent rather than zero when the payload did not carry them
                    * — a "₹0 over the ₹0 limit" line is a claim, and a false one.
                    */}
                  {row.consignmentPaise === null || row.thresholdPaise === null ? null : (
                    <span className="text-muted-foreground text-sm tabular-nums">
                      {t("eway.pendingOver", {
                        amount: formatRupees(row.consignmentPaise),
                        threshold: formatRupees(row.thresholdPaise),
                      })}
                    </span>
                  )}

                  {/*
                    * `flaggedAt` IS AN ISO INSTANT, not a calendar day. Slicing
                    * it to ten characters takes the UTC day and prints it as an
                    * IST one, so a bill saved between 00:00 and 05:30 IST would
                    * report yesterday. `istDayOfInstant` first, always.
                    */}
                  <span className="text-muted-foreground text-xs">
                    {t("eway.pendingSince", { date: formatDay(istDayOfInstant(row.flaggedAt)) })}
                  </span>
                </span>

                <ChevronRightIcon aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>

        {/*
          * SAY SO WHEN THE LIST IS SHORT OF THE TRUTH. Under-reporting a
          * compliance queue silently is the one failure this panel must not
          * have: "two are waiting" when four are is worse than no panel at all,
          * because it is believed.
          */}
        {unverified === 0 ? null : (
          <p className="text-muted-foreground px-4 py-3 text-sm">
            {t("eway.pendingUnverified", { count: unverified })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
